import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { worksiteApi, businessApi } from '../lib/api'
import type { SaveWorksiteRequest, WorksiteSearchParams, SaveBusinessRequest, BusinessSearchParams, MergeRequest, SaveArrivalInstructionRequest, CopyArrivalInstructionRequest } from '../types/business'

// === Worksite Hooks ===

export function useWorksiteSearch(params?: WorksiteSearchParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['worksites', params],
    queryFn: async () => {
      const response = await worksiteApi.search(params)
      return response.data
    },
    ...(options?.enabled !== undefined ? { enabled: options.enabled } : {}),
  })
}

export function useWorksiteDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['worksites', id],
    queryFn: async () => {
      if (!id) throw new Error('Worksite ID is required')
      const response = await worksiteApi.getById(id)
      return response.data
    },
    enabled: !!id,
  })
}

export function useCreateWorksite() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SaveWorksiteRequest) =>
      worksiteApi.create(data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['worksites'] })
    },
  })
}

export function useUpdateWorksite(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SaveWorksiteRequest) =>
      worksiteApi.update(id, data).then(r => r.data),
    onSuccess: (data) => {
      queryClient.setQueryData(['worksites', id], data)
      queryClient.invalidateQueries({ queryKey: ['worksites'] })
    },
  })
}

export function useDeleteWorksite() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => worksiteApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['worksites'] })
    },
  })
}

export function useImportEmployment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ file, options }: {
      file: File
      options?: {
        updateExisting?: boolean
        createMissingWorksites?: boolean
        createMissingUnions?: boolean
        defaultWorksiteType?: string
        maxRecords?: number
      }
    }) => worksiteApi.importCsv(file, options).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['worksites'] })
      queryClient.invalidateQueries({ queryKey: ['employment'] })
      queryClient.invalidateQueries({ queryKey: ['unions'] })
    },
  })
}

export function useLinkWorksiteToBusiness() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ worksiteId, businessId, unionOverrides }: { worksiteId: string; businessId: string; unionOverrides?: import('../types/business').UnionCoverageOverride[] }) =>
      worksiteApi.linkToBusiness(worksiteId, businessId, unionOverrides),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['worksites'] })
      queryClient.invalidateQueries({ queryKey: ['businesses'] })
    },
  })
}

export function useWorksiteLocations() {
  return useQuery({
    queryKey: ['worksites', 'locations'],
    queryFn: async () => {
      const response = await worksiteApi.getLocations()
      return response.data
    },
  })
}

export function useWorksiteSuggest(query: string, businessId?: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['worksites', 'suggest', query, businessId],
    queryFn: async () => {
      const response = await worksiteApi.suggest(query, businessId)
      return response.data
    },
    enabled: (options?.enabled ?? true) && query.length >= 2,
  })
}

// === Business Hooks (parent grouping entity) ===

export function useBusinesses() {
  return useQuery({
    queryKey: ['businesses'],
    queryFn: async () => {
      const response = await businessApi.getAll()
      return response.data
    },
  })
}

export function useCreateBusiness() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SaveBusinessRequest) =>
      businessApi.create(data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['businesses'] })
    },
  })
}

export function useUpdateBusiness(id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SaveBusinessRequest) =>
      businessApi.update(id, data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['businesses'] })
    },
  })
}

export function useDeleteBusiness() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => businessApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['businesses'] })
    },
  })
}

export function useMergeBusinesses() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: MergeRequest) =>
      businessApi.merge(data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['businesses'] })
      queryClient.invalidateQueries({ queryKey: ['worksites'] })
    },
  })
}

export function useMergeWorksites() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: MergeRequest) =>
      worksiteApi.merge(data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['worksites'] })
      queryClient.invalidateQueries({ queryKey: ['businesses'] })
    },
  })
}

export function useBusinessSuggest(query: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['businesses', 'suggest', query],
    queryFn: async () => {
      const response = await businessApi.suggest(query)
      return response.data
    },
    enabled: (options?.enabled ?? true) && query.length >= 2,
  })
}

export function useBusinessSearch(params?: BusinessSearchParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['businesses', 'search', params],
    queryFn: async () => {
      const response = await businessApi.search(params)
      return response.data
    },
    ...(options?.enabled !== undefined ? { enabled: options.enabled } : {}),
  })
}

export function useBusinessDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['businesses', id, 'detail'],
    queryFn: async () => {
      if (!id) throw new Error('Business ID is required')
      const response = await businessApi.getDetail(id)
      return response.data
    },
    enabled: !!id,
  })
}

export function useBusinessEmployees(id: string | undefined, params?: { page?: number; pageSize?: number; search?: string }) {
  return useQuery({
    queryKey: ['businesses', id, 'employees', params],
    queryFn: async () => {
      if (!id) throw new Error('Business ID is required')
      const response = await businessApi.getEmployees(id, params)
      return response.data
    },
    enabled: !!id,
    placeholderData: keepPreviousData,
  })
}

// === Arrival instructions (O3) ===

export type ArrivalKind = 'worksite' | 'business'

function arrivalRoot(kind: ArrivalKind) {
  return kind === 'worksite' ? 'worksites' : 'businesses'
}

function arrivalApi(kind: ArrivalKind) {
  return kind === 'worksite' ? worksiteApi : businessApi
}

export function arrivalKey(kind: ArrivalKind, id: string | undefined) {
  return [arrivalRoot(kind), id, 'arrival'] as const
}

/** Invalidate every cached arrival view of this kind. A business (chain) change also reaches
 *  worksites, because a worksite's effective instruction can come from its chain. */
function invalidateArrivalViews(queryClient: ReturnType<typeof useQueryClient>, kind: ArrivalKind) {
  const roots = kind === 'business' ? ['businesses', 'worksites'] : ['worksites']
  queryClient.invalidateQueries({
    predicate: (q) => roots.includes(q.queryKey[0] as string) && q.queryKey[2] === 'arrival',
  })
}

export function useArrivalInstruction(kind: ArrivalKind, id: string | undefined) {
  return useQuery({
    queryKey: arrivalKey(kind, id),
    queryFn: async () => {
      if (!id) throw new Error('ID is required')
      const response = await arrivalApi(kind).getArrival(id)
      return response.data
    },
    enabled: !!id,
  })
}

export function useSaveArrivalInstruction(kind: ArrivalKind, id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: SaveArrivalInstructionRequest) =>
      arrivalApi(kind).saveArrival(id, data).then(r => r.data),
    onSuccess: (view) => {
      queryClient.setQueryData(arrivalKey(kind, id), view)
      if (kind === 'business') invalidateArrivalViews(queryClient, kind)
      else queryClient.invalidateQueries({ queryKey: arrivalKey(kind, id) })
    },
  })
}

export function useCopyArrivalInstruction(kind: ArrivalKind, id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CopyArrivalInstructionRequest) =>
      arrivalApi(kind).copyArrival(id, data).then(r => r.data),
    onSuccess: (_result, variables) => {
      if (variables.dryRun) return
      // A real copy rewrites the targets' rows; refresh every cached arrival view it can reach.
      invalidateArrivalViews(queryClient, kind)
    },
  })
}
